import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  RiArrowLeftLine,
  RiPlaneLine,
  RiBuildingLine,
  RiMapPinLine,
  RiStarFill,
  RiShieldCheckLine,
  RiTeamLine,
  RiMailLine,
  RiDashboard3Line,
  RiPriceTag3Line,
  RiCalendarEventLine,
  RiCircleFill,
  RiCheckLine,
  RiMedalLine,
  RiAwardLine,
  RiCheckboxCircleFill
} from '@remixicon/react';
import { Card, CardContent } from '@components/ui/Card';
import { Avatar, AvatarFallback } from '@components/ui/Avatar';
import { TrustScore } from '@/features/trust-score';

/**
 * AircraftDetail Page
 * Displays detailed information about an aircraft
 */
const AircraftDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tailNumber } = useParams();

  // Get aircraft data from navigation state or fetch if needed
  const aircraft = location.state?.aircraft || {};

  // Extract data with fallbacks
  const {
    tail_number = tailNumber,
    aircraft_type,
    operator_name,
    operator_email,
    operator_location,
    trust_score,
    quote_price,
    route_from,
    route_to,
    message,
    badges = ["PLATINUM", "WINGMAN"],
    fleet_count,
    operator,
    received_date,
    passenger_capacity,
    range_nm,
    hourly_rate,
    mfr_year,
    model,
    make_model
  } = aircraft;

  // Use operator object if available
  const displayOperatorName = operator?.name || operator_name || 'Unknown Operator';
  const displayOperatorEmail = operator?.email || operator_email || '';
  const displayOperatorLocation = operator?.location || operator_location || '';
  const displayTrustScore = operator?.rating_tiers;
  const trustScore = operator?.trust_score;

  // Format currency
  const formatPrice = (price) => {
    if (!price) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Get operator initials
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <div>
      {/* Main Content */}
      <main className="max-w-7xl px-4 sm:px-6 lg:px-8 py-5">
        {/* Title Section */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Aircraft Available: {tail_number}
          </h1>
        </div>

        {/* Operator Header Card */}
        <Card className="overflow-hidden mb-4">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <h1 className="text-2xl text-primary-500 font-semibold">{tail_number}</h1>
                <div className="flex gap-1 text-sm text-gray-500 mt-3">
                  <RiPlaneLine className="h-4 w-4 text-gray-600"/> {model}
                </div>
                <div className="flex gap-1 text-sm text-gray-500">
                  <RiBuildingLine className="h-4 w-4 text-gray-600"/> {operator.faa_city} <RiCircleFill className="h-2 w-2 mt-[5px] text-gray-600"/> {operator.faa_state}
                </div>
              </div>
              
              {/* Status Badge */}
              <div className="flex-shrink-0">
                <span className="inline-flex items-center border border-gray-200 px-3 py-1.5 rounded-md text-xs font-medium text-gray-500">
                  <RiCheckboxCircleFill className="h-4 w-4 text-green-500"/> Available
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Operator Details Section */}
        <Card className="overflow-hidden mb-4">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Operator Details</p>

            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <Avatar className="w-14 h-14 border-blue-500">
                  <AvatarFallback className="bg-blue-100 text-blue-700 font-semibold text-lg">
                    {getInitials(displayOperatorName)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">{displayOperatorName}</h3>
                  {displayOperatorLocation && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <RiMapPinLine className="w-3 h-3 text-gray-400" />
                      <span className="text-sm text-gray-500">{displayOperatorLocation}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Trust Score */}
              <div className="scale-75 mt-[-30px]">
              <TrustScore score={displayTrustScore} numericScore={trustScore ?? null} />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
                  <span className="px-2.5 py-1 bg-gray-200 text-[#171717] text-[10px] font-semibold rounded-2xl flex items-center gap-1">
                    <RiMedalLine className="h-5 w-5" /> PLATINUM
                  </span>
                  <span className="px-2.5 py-1 bg-gray-200 text-[#171717] text-[10px] font-semibold rounded-2xl flex items-center gap-1">
                    <RiAwardLine className="h-5 w-5" /> WINGMAN
                  </span>
                  <span className="px-2.5 py-1 bg-green-100 text-green-700 text-[10px] font-semibold rounded-2xl flex items-center gap-1">
                    <RiCheckLine className="h-5 w-5" /> NO AOG
                  </span>
            </div>
          </CardContent>
        </Card>

         <Card className="overflow-hidden mb-4">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Aircraft Details</p>


              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Passenger Count */}
                <div className="flex items-start gap-2.5">
                  <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                    <RiPlaneLine className="h-4 w-4 text-gray-600"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-600 mb-1">Aircraft Type</p>
                    <p className="text-base font-semibold text-gray-900">
                     {aircraft_type || 'Not defined'}
                    </p>
                  </div>
                </div>

                {/* Round Trip */}
                <div className="flex items-start gap-2.5">
                  <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                    <RiTeamLine className="h-4 w-4 text-gray-600"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-600 mb-1">Seating Capacity</p>
                    <p className="text-base font-semibold text-gray-900">
                      {passenger_capacity || 0} Passengers
                    </p>
                  </div>
                </div>
              </div>


              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Passenger Count */}
                <div className="flex items-start gap-2.5">
                  <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                    <RiDashboard3Line className="h-4 w-4 text-gray-600"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-600 mb-1">Range</p>
                    <p className="text-base font-semibold text-gray-900">
                     {range_nm || 'Not defined'}
                    </p>
                  </div>
                </div>

                {/* Round Trip */}
                <div className="flex items-start gap-2.5">
                  <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                    <RiPriceTag3Line className="h-4 w-4 text-gray-600"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-600 mb-1">Hourly Rate</p>
                    <p className="text-base font-semibold text-gray-900">
                      {hourly_rate || "Not defined"} 
                    </p>
                  </div>
                </div>


                <div className="flex items-start gap-2.5">
                  <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-2xl flex-shrink-0">
                    <RiCalendarEventLine className="h-4 w-4 text-gray-600"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-600 mb-1">Year of Manufacture</p>
                    <p className="text-base font-semibold text-gray-900">
                      {mfr_year || "Not defined"}
                    </p>
                  </div>
                </div>
              </div>

          </CardContent>
        </Card>

        {/* Aircraft Type */}
        {aircraft_type && (
          <Card className="overflow-hidden mb-4">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Aircraft Type</p>
              <div className="flex items-center gap-2">
                <RiPlaneLine className="w-5 h-5 text-gray-400" />
                <p className="font-medium text-gray-900">{aircraft_type}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Message Section */}
        {message && (
          <Card className="overflow-hidden mb-6">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Message</p>
              <div className="prose prose-sm max-w-none text-gray-700">
                {message.split('\n').map((line, index) => (
                  <p key={index} className="mb-2">{line}</p>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

       
        <div className="flex items-center gap-3 pb-6">
           {/*<button
            onClick={() => navigate(-1)}
            className="flex-1 h-12 px-4 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            <RiPlaneLine className="w-5 h-5" />
            Tail Details
          </button>*/}
          <button
            //onClick={handleShortlist}
            disabled={true}
            className="flex-1 h-12 px-4 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 opacity-50 cursor-not-allowed"
          >
            <RiStarFill className="w-5 h-5" />
            Shortlist {tail_number}
          </button>
        </div>
      </main>
    </div>
  );
};

export default AircraftDetail;
